import React, { useMemo, useState } from 'react';
import {
  ActionSheetIOS,
  ActivityIndicator,
  Alert,
  FlatList,
  Image,
  Platform,
  RefreshControl,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import { launchCamera, launchImageLibrary } from 'react-native-image-picker';
import { useUploadImageMutation } from '../redux/services/acompanyApi';
import { ShowToast, resolveImageUrl } from '../Common/common';

import T from '../Components/T';
import { tp } from '../utils/translateText';
export interface ProductStockItem {
  id: number;
  name: string;
  code: string;
  uom: string;
  stock: number;
  minStock?: number;
  image: string;
}

interface Props {
  title: string;
  items: ProductStockItem[];
  isLoading: boolean;
  onRefresh: () => Promise<any>;
  // Persists the uploaded key as the product's pr_image; must reject on failure.
  saveImage: (id: number, key: string) => Promise<any>;
  onBack?: () => void;
}

const ProductStockList = ({ title, items, isLoading, onRefresh, saveImage, onBack }: Props) => {
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  // Local previews so the new photo shows immediately, before the refetch lands.
  const [previews, setPreviews] = useState<Record<number, string>>({});
  const [uploadImage] = useUploadImageMutation();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      i => i.name.toLowerCase().includes(q) || i.code.toLowerCase().includes(q),
    );
  }, [items, search]);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  const upload = async (item: ProductStockItem, asset: any, webFile?: File) => {
    setBusyId(item.id);
    setPreviews(p => ({ ...p, [item.id]: webFile ? URL.createObjectURL(webFile) : asset.uri }));
    try {
      const formData = new FormData();
      if (webFile) {
        formData.append('file', webFile);
      } else {
        formData.append('file', {
          uri: asset.uri,
          type: asset.type || 'image/jpeg',
          name: asset.fileName || `product_${item.id}.jpg`,
        } as any);
      }
      formData.append('folder', 'products');
      formData.append('fileName', `product_${item.id}_${Date.now()}`);
      const res = await uploadImage(formData).unwrap();
      if (!res?.key) throw new Error('No key returned');
      await saveImage(item.id, res.key);
      ShowToast('Photo saved');
    } catch (err: any) {
      ShowToast(err?.data?.message || err?.message || 'Failed to save photo', 'error');
      setPreviews(p => {
        const n = { ...p };
        delete n[item.id];
        return n;
      });
    } finally {
      setBusyId(null);
    }
  };

  const pick = (item: ProductStockItem, source: 'camera' | 'gallery') => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      if (source === 'camera') input.setAttribute('capture', 'environment');
      input.onchange = (e: any) => {
        const file = e.target.files?.[0];
        if (file) upload(item, null, file);
      };
      input.click();
      return;
    }
    const cb = (response: any) => {
      if (response.didCancel) return;
      if (response.errorCode) {
        ShowToast(response.errorMessage || 'Could not open picker', 'error');
        return;
      }
      const asset = response.assets?.[0];
      if (asset?.uri) upload(item, asset);
    };
    if (source === 'camera') {
      launchCamera({ mediaType: 'photo', cameraType: 'back', quality: 0.8, saveToPhotos: false }, cb);
    } else {
      launchImageLibrary({ mediaType: 'photo', quality: 0.8 }, cb);
    }
  };

  const choose = (item: ProductStockItem) => {
    if (Platform.OS === 'web') {
      pick(item, 'camera');
      return;
    }
    if (Platform.OS === 'ios') {
      ActionSheetIOS.showActionSheetWithOptions(
        { options: ['Cancel', 'Take Photo', 'Choose from Gallery'], cancelButtonIndex: 0 },
        idx => {
          if (idx === 1) pick(item, 'camera');
          if (idx === 2) pick(item, 'gallery');
        },
      );
      return;
    }
    Alert.alert(item.name, tp("Product photo"), [
      { text: 'Take Photo', onPress: () => pick(item, 'camera') },
      { text: 'Choose from Gallery', onPress: () => pick(item, 'gallery') },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  const renderItem = ({ item }: { item: ProductStockItem }) => {
    const uri = previews[item.id] || resolveImageUrl(item.image);
    const low = item.stock <= 0 || (item.minStock ? item.stock <= item.minStock : false);
    return (
      <View style={styles.row}>
        <TouchableOpacity onPress={() => choose(item)} disabled={busyId === item.id} style={styles.thumbWrap}>
          {uri ? (
            <Image source={{ uri }} style={styles.thumb} resizeMode="cover" />
          ) : (
            <View style={[styles.thumb, styles.thumbEmpty]}>
              <FontAwesome name="camera" size={22} color="#9aa0a6" />
            </View>
          )}
          {busyId === item.id ? (
            <View style={styles.thumbBusy}>
              <ActivityIndicator color="#fff" />
            </View>
          ) : null}
        </TouchableOpacity>
        <View style={styles.info}>
          <Text style={styles.name} numberOfLines={2}>{item.name}</Text>
          <Text style={styles.sub}>{[item.code, item.uom].filter(Boolean).join(' · ')}</Text>
        </View>
        <View style={styles.stockBox}>
          <Text style={[styles.stock, low && styles.stockLow]}>{item.stock}</Text>
          <Text style={styles.stockLabel}><T>in stock</T></Text>
        </View>
        <TouchableOpacity onPress={() => choose(item)} disabled={busyId === item.id} style={styles.camBtn}>
          <FontAwesome name="camera" size={20} color="#1e63d6" />
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        {onBack ? (
          <TouchableOpacity onPress={onBack} style={styles.backBtn}>
            <FontAwesome name="arrow-left" size={18} color="#fff" />
          </TouchableOpacity>
        ) : null}
        <Text style={styles.title}>{title}</Text>
      </View>
      <TextInput
        style={styles.search}
        placeholder={tp("Search by name or code")}
        value={search}
        onChangeText={setSearch}
        autoCorrect={false}
      />
      {isLoading ? (
        <ActivityIndicator style={{ marginTop: 32 }} />
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={i => String(i.id)}
          renderItem={renderItem}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={doRefresh} />}
          ListEmptyComponent={<Text style={styles.empty}><T>No products found</T></Text>}
          contentContainerStyle={{ paddingBottom: 24 }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f4f6f8' },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#1e63d6', paddingHorizontal: 16, paddingVertical: 14 },
  backBtn: { marginRight: 14 },
  title: { color: '#fff', fontSize: 18, fontWeight: '600' },
  search: { backgroundColor: '#fff', margin: 12, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, borderWidth: 1, borderColor: '#dfe3e8' },
  row: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', marginHorizontal: 12, marginBottom: 8, padding: 10, borderRadius: 10 },
  thumbWrap: { width: 56, height: 56, marginRight: 10 },
  thumb: { width: 56, height: 56, borderRadius: 8, backgroundColor: '#eceff1' },
  thumbEmpty: { alignItems: 'center', justifyContent: 'center' },
  thumbBusy: { ...StyleSheet.absoluteFillObject, borderRadius: 8, backgroundColor: 'rgba(0,0,0,0.45)', alignItems: 'center', justifyContent: 'center' },
  info: { flex: 1 },
  name: { fontSize: 15, fontWeight: '600', color: '#202124' },
  sub: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  stockBox: { alignItems: 'flex-end', marginHorizontal: 10 },
  stock: { fontSize: 17, fontWeight: '700', color: '#188038' },
  stockLow: { color: '#d93025' },
  stockLabel: { fontSize: 10, color: '#6b7280' },
  camBtn: { padding: 8 },
  empty: { textAlign: 'center', marginTop: 32, color: '#6b7280' },
});

export default ProductStockList;
